"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { AudioLines, MessageSquare, ChevronUp, LogOut, ExternalLink, FileTextIcon, Search, Plus, Users, ImagePlus, Film, PenTool } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogoIcon } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrgShares } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { useAppConfig } from "@/lib/config";
import { DEFAULT_NAV_ITEM_IDS, filterEnabledNavItemIds, getSidebarLabels, getSidebarNavItemsLocalized, type SidebarNavIcon } from "@/lib/sidebar-nav";
import { cn } from "@/lib/utils";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrgSearchDialog } from "./org-search-dialog";
import { OrganizationSwitcher } from "./organization-switcher";
import type { Organization } from "@/lib/types";
interface OrgSidebarProps {
  organizationId: string;
  currentShareId?: string;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (org: Organization | null) => void;
  className?: string;
}
interface OrgShareItem {
  id: string;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}
type OrgShareHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "share";
  key: string;
  share: OrgShareItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 32;
const ROW_HEIGHT_SPACER = 16;
const ROW_HEIGHT_SHARE = 60;
interface OrgShareHistoryRowProps {
  rows: OrgShareHistoryRow[];
  currentShareId?: string;
  onShareSelect: (shareId: string) => void;
}
function getOrgShareRowHeight(index: number, {
  rows
}: OrgShareHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_SHARE;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupSharesByDate(shares: OrgShareItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as OrgShareItem[],
    yesterday: [] as OrgShareItem[],
    lastWeek: [] as OrgShareItem[],
    older: [] as OrgShareItem[]
  };
  shares.forEach(share => {
    const shareDate = new Date(share.updatedAt);
    if (shareDate.toDateString() === today.toDateString()) {
      groups.today.push(share);
    } else if (shareDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(share);
    } else if (shareDate >= lastWeek) {
      groups.lastWeek.push(share);
    } else {
      groups.older.push(share);
    }
  });
  return groups;
}
function renderSidebarIcon(icon: SidebarNavIcon) {
  switch (icon) {
    case "message-square":
      return <MessageSquare className="h-4 w-4" />;
    case "folder":
      return <FileTextIcon className="h-4 w-4" />;
    case "users":
      return <Users className="h-4 w-4" />;
    case "image":
      return <ImagePlus className="h-4 w-4" />;
    case "film":
      return <Film className="h-4 w-4" />;
    case "audio-lines":
      return <AudioLines className="h-4 w-4" />;
    case "pen-tool":
      return <PenTool className="h-4 w-4" />;
  }
}
function OrgShareRowComponent(t0) {
  const $ = _c(29);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentShareId,
    onShareSelect
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    share
  } = row;
  const t1 = currentShareId === share.id;
  let t2;
  if ($[8] !== onShareSelect || $[9] !== share.id) {
    t2 = () => onShareSelect(share.id);
    $[8] = onShareSelect;
    $[9] = share.id;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== share.title) {
    t4 = <div className="truncate text-sm font-medium mb-0.5">{share.title}</div>;
    $[12] = share.title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== share.updatedAt) {
    t5 = formatDate(share.updatedAt);
    $[14] = share.updatedAt;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <div className="text-xs text-muted-foreground">{t5}</div>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <div className="flex-1 min-w-0">{t4}{t6}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t7) {
    t8 = <div className="relative h-full px-2 pb-1"><div className="relative h-full"><SidebarMenuButton isActive={t1} onClick={t2} className="h-full! w-full justify-start gap-3 group relative" type="button">{t3}{t7}</SidebarMenuButton></div></div>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== ariaAttributes || $[26] !== style || $[27] !== t8) {
    t9 = <div {...ariaAttributes} style={style}>{t8}</div>;
    $[25] = ariaAttributes;
    $[26] = style;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
export function OrgSidebar(t0) {
  const $ = _c(167);
  const {
    organizationId,
    currentShareId,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className
  } = t0;
  const listContainerRef = useRef(null);
  const queryClient = useQueryClient();
  const router = useRouter();
  const posthog = usePostHog();
  const {
    t
  } = useTranslation();
  const {
    enabledNavItems,
    loginUrl,
    dashboardUrl
  } = useAppConfig();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    isLoading: isOrgLoading
  } = useOrganization();
  const {
    data: orgSharesData,
    isLoading: isSharesLoading
  } = useOrgShares(organizationId);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  orgSharesData?.shares;
  let t1;
  if ($[0] !== orgSharesData?.shares) {
    t1 = orgSharesData?.shares ?? [];
    $[0] = orgSharesData?.shares;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shares = t1;
  let t2;
  if ($[2] !== loginUrl || $[3] !== posthog || $[4] !== queryClient || $[5] !== router || $[6] !== signOut) {
    t2 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(loginUrl);
          }
        }
      });
    };
    $[2] = loginUrl;
    $[3] = posthog;
    $[4] = queryClient;
    $[5] = router;
    $[6] = signOut;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const logout = t2;
  let t3;
  if ($[8] !== organizationId || $[9] !== router) {
    t3 = shareId => {
      router.push(`/org/${organizationId}/chat/${shareId}`);
    };
    $[8] = organizationId;
    $[9] = router;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleShareSelect = t3;
  let t4;
  if ($[11] !== shares) {
    t4 = groupSharesByDate([...shares].sort(_temp));
    $[11] = shares;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const shareGroups = t4;
  let t5;
  if ($[13] !== t) {
    t5 = getSidebarLabels(t);
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const labels = t5;
  let rows;
  if ($[15] !== labels.timeGroups.last7Days || $[16] !== labels.timeGroups.older || $[17] !== labels.timeGroups.today || $[18] !== labels.timeGroups.yesterday || $[19] !== shareGroups.lastWeek || $[20] !== shareGroups.older || $[21] !== shareGroups.today || $[22] !== shareGroups.yesterday) {
    const groups = [{
      title: labels.timeGroups.today,
      shares: shareGroups.today
    }, {
      title: labels.timeGroups.yesterday,
      shares: shareGroups.yesterday
    }, {
      title: labels.timeGroups.last7Days,
      shares: shareGroups.lastWeek
    }, {
      title: labels.timeGroups.older,
      shares: shareGroups.older
    }];
    rows = [];
    groups.forEach((t6, groupIndex) => {
      const {
        title,
        shares: groupedShares
      } = t6;
      if (groupedShares.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedShares.forEach(share => {
        rows.push({
          type: "share",
          key: `share-${share.id}`,
          share
        });
      });
      const hasNextGroupWithShares = groups.slice(groupIndex + 1).some(_temp2);
      if (hasNextGroupWithShares) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[15] = labels.timeGroups.last7Days;
    $[16] = labels.timeGroups.older;
    $[17] = labels.timeGroups.today;
    $[18] = labels.timeGroups.yesterday;
    $[19] = shareGroups.lastWeek;
    $[20] = shareGroups.older;
    $[21] = shareGroups.today;
    $[22] = shareGroups.yesterday;
    $[23] = rows;
  } else {
    rows = $[23];
  }
  const historyRows = rows;
  let t6;
  if ($[24] !== currentShareId || $[25] !== handleShareSelect || $[26] !== historyRows) {
    t6 = {
      rows: historyRows,
      currentShareId,
      onShareSelect: handleShareSelect
    };
    $[24] = currentShareId;
    $[25] = handleShareSelect;
    $[26] = historyRows;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const rowProps = t6;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  let t7;
  if ($[28] !== enabledNavItems || $[29] !== t) {
    t7 = getSidebarNavItemsLocalized(filterEnabledNavItemIds(DEFAULT_NAV_ITEM_IDS, enabledNavItems), t);
    $[28] = enabledNavItems;
    $[29] = t;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const navItems = t7;
  if (isUserLoading) {
    let t8;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    return t8;
  }
  if (!user) {
    let t8;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <LogoIcon className="!size-9" />;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    let t9;
    if ($[33] !== labels.brand) {
      t9 = <h1 className="text-xl font-semibold">{labels.brand}</h1>;
      $[33] = labels.brand;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] !== t) {
      t10 = t("sidebar.nav.chat", {
        defaultValue: "Chat"
      });
      $[35] = t;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    let t11;
    if ($[37] !== t10) {
      t11 = <Badge>{t10}</Badge>;
      $[37] = t10;
      $[38] = t11;
    } else {
      t11 = $[38];
    }
    let t12;
    if ($[39] !== t11 || $[40] !== t9) {
      t12 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t8}{t9}{t11}</Link>;
      $[39] = t11;
      $[40] = t9;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    let t13;
    if ($[42] !== labels.signInRequired) {
      t13 = <div className="font-medium mb-2">{labels.signInRequired}</div>;
      $[42] = labels.signInRequired;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    let t14;
    if ($[44] !== labels.signInForOrgSidebar) {
      t14 = <p className="text-muted-foreground mb-3">{labels.signInForOrgSidebar}</p>;
      $[44] = labels.signInForOrgSidebar;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    let t15;
    if ($[46] !== labels.signIn) {
      t15 = <Button size="sm" asChild={true}><Link href="/login">{labels.signIn}</Link></Button>;
      $[46] = labels.signIn;
      $[47] = t15;
    } else {
      t15 = $[47];
    }
    let t16;
    if ($[48] !== labels.createAccount) {
      t16 = <Button size="sm" variant="outline" asChild={true}><Link href="/signup">{labels.createAccount}</Link></Button>;
      $[48] = labels.createAccount;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    let t17;
    if ($[50] !== t15 || $[51] !== t16) {
      t17 = <div className="flex items-center justify-end gap-2">{t15}{t16}</div>;
      $[50] = t15;
      $[51] = t16;
      $[52] = t17;
    } else {
      t17 = $[52];
    }
    let t18;
    if ($[53] !== t13 || $[54] !== t14 || $[55] !== t17) {
      t18 = <div className="w-full rounded-md border p-4 text-sm">{t13}{t14}{t17}</div>;
      $[53] = t13;
      $[54] = t14;
      $[55] = t17;
      $[56] = t18;
    } else {
      t18 = $[56];
    }
    let t19;
    if ($[57] !== t12 || $[58] !== t18) {
      t19 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t12}{t18}</div></SidebarHeader>;
      $[57] = t12;
      $[58] = t18;
      $[59] = t19;
    } else {
      t19 = $[59];
    }
    let t20;
    if ($[60] !== className || $[61] !== t19) {
      t20 = <Sidebar className={className}>{t19}</Sidebar>;
      $[60] = className;
      $[61] = t19;
      $[62] = t20;
    } else {
      t20 = $[62];
    }
    return t20;
  }
  if (isSharesLoading || isOrgLoading) {
    let t8;
    if ($[63] !== isOrgLoading || $[64] !== selectedOrganization) {
      t8 = <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
      $[63] = isOrgLoading;
      $[64] = selectedOrganization;
      $[65] = t8;
    } else {
      t8 = $[65];
    }
    return t8;
  }
  let t8;
  if ($[66] !== className) {
    t8 = cn(className, "max-md:hidden");
    $[66] = className;
    $[67] = t8;
  } else {
    t8 = $[67];
  }
  let t9;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LogoIcon className="!size-9" />;
    $[68] = t9;
  } else {
    t9 = $[68];
  }
  let t10;
  if ($[69] !== labels.brand) {
    t10 = <Link href="/" prefetch={true}>{t9}<span className="text-lg font-bold tracking-tight">{labels.brand}</span></Link>;
    $[69] = labels.brand;
    $[70] = t10;
  } else {
    t10 = $[70];
  }
  let t11;
  if ($[71] !== labels.brand || $[72] !== t10) {
    t11 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip={labels.brand}>{t10}</SidebarMenuButton></SidebarMenuItem>;
    $[71] = labels.brand;
    $[72] = t10;
    $[73] = t11;
  } else {
    t11 = $[73];
  }
  let t12;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Plus className="h-4 w-4" />;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  let t13;
  if ($[75] !== labels.newChat) {
    t13 = <Link href="/">{t12}<span>{labels.newChat}</span></Link>;
    $[75] = labels.newChat;
    $[76] = t13;
  } else {
    t13 = $[76];
  }
  let t14;
  if ($[77] !== labels.newChat || $[78] !== t13) {
    t14 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={labels.newChat} className="border border-border">{t13}</SidebarMenuButton></SidebarMenuItem>;
    $[77] = labels.newChat;
    $[78] = t13;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] !== navItems) {
    t15 = navItems.map(_temp3);
    $[80] = navItems;
    $[81] = t15;
  } else {
    t15 = $[81];
  }
  let t16;
  if ($[82] !== t11 || $[83] !== t14 || $[84] !== t15) {
    t16 = <SidebarHeader><SidebarMenu>{t11}{t14}{t15}</SidebarMenu></SidebarHeader>;
    $[82] = t11;
    $[83] = t14;
    $[84] = t15;
    $[85] = t16;
  } else {
    t16 = $[85];
  }
  let t17;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[86] = t17;
  } else {
    t17 = $[86];
  }
  let t18;
  if ($[87] !== onSelectOrganization || $[88] !== organizations || $[89] !== selectedOrganization) {
    t18 = organizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[87] = onSelectOrganization;
    $[88] = organizations;
    $[89] = selectedOrganization;
    $[90] = t18;
  } else {
    t18 = $[90];
  }
  let t19;
  let t20;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsSearchOpen(true);
    t20 = <Search className="h-4 w-4" />;
    $[91] = t19;
    $[92] = t20;
  } else {
    t19 = $[91];
    t20 = $[92];
  }
  let t21;
  if ($[93] !== labels.searchChats) {
    t21 = <span>{labels.searchChats}</span>;
    $[93] = labels.searchChats;
    $[94] = t21;
  } else {
    t21 = $[94];
  }
  let t22;
  if ($[95] !== labels.searchChats || $[96] !== t21) {
    t22 = <SidebarMenu className="px-2"><SidebarMenuItem><SidebarMenuButton type="button" tooltip={labels.searchChats} onClick={t19}>{t20}{t21}</SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[95] = labels.searchChats;
    $[96] = t21;
    $[97] = t22;
  } else {
    t22 = $[97];
  }
  let t23;
  if ($[98] !== t18 || $[99] !== t22) {
    t23 = <div>{t17}{t18}{t22}</div>;
    $[98] = t18;
    $[99] = t22;
    $[100] = t23;
  } else {
    t23 = $[100];
  }
  let t24;
  if ($[101] !== historyRows || $[102] !== labels.noSharedChats || $[103] !== labels.shareChatHint || $[104] !== rowProps || $[105] !== shares.length) {
    t24 = shares.length === 0 ? <div className="flex flex-col items-center justify-center py-8 px-4 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">{labels.noSharedChats}</p><p className="text-xs text-muted-foreground">{labels.shareChatHint}</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={OrgShareRowComponent} rowCount={historyRows.length} rowHeight={getOrgShareRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[101] = historyRows;
    $[102] = labels.noSharedChats;
    $[103] = labels.shareChatHint;
    $[104] = rowProps;
    $[105] = shares.length;
    $[106] = t24;
  } else {
    t24 = $[106];
  }
  let t25;
  if ($[107] !== isHistoryHidden || $[108] !== t24) {
    t25 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t24}</div>;
    $[107] = isHistoryHidden;
    $[108] = t24;
    $[109] = t25;
  } else {
    t25 = $[109];
  }
  let t26;
  if ($[110] !== t23 || $[111] !== t25) {
    t26 = <SidebarContent className="overflow-hidden pb-2">{t23}{t25}</SidebarContent>;
    $[110] = t23;
    $[111] = t25;
    $[112] = t26;
  } else {
    t26 = $[112];
  }
  let t27;
  if ($[113] !== isOrgLoading || $[114] !== selectedOrganization) {
    t27 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={isOrgLoading} /></div>;
    $[113] = isOrgLoading;
    $[114] = selectedOrganization;
    $[115] = t27;
  } else {
    t27 = $[115];
  }
  const t28 = user?.name ?? labels.user;
  let t29;
  if ($[116] !== user?.name) {
    t29 = user?.name?.split(" ").map(_temp4).join("").toUpperCase().slice(0, 2) ?? "U";
    $[116] = user?.name;
    $[117] = t29;
  } else {
    t29 = $[117];
  }
  let t30;
  if ($[118] !== t29) {
    t30 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t29}</span></div>;
    $[118] = t29;
    $[119] = t30;
  } else {
    t30 = $[119];
  }
  const t31 = user?.name;
  let t32;
  if ($[120] !== t31) {
    t32 = <span className="truncate font-semibold">{t31}</span>;
    $[120] = t31;
    $[121] = t32;
  } else {
    t32 = $[121];
  }
  const t33 = user?.email;
  let t34;
  if ($[122] !== t33) {
    t34 = <span className="truncate text-xs text-muted-foreground">{t33}</span>;
    $[122] = t33;
    $[123] = t34;
  } else {
    t34 = $[123];
  }
  let t35;
  if ($[124] !== t32 || $[125] !== t34) {
    t35 = <div className="grid flex-1 text-left text-sm leading-tight">{t32}{t34}</div>;
    $[124] = t32;
    $[125] = t34;
    $[126] = t35;
  } else {
    t35 = $[126];
  }
  let t36;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[127] = t36;
  } else {
    t36 = $[127];
  }
  let t37;
  if ($[128] !== t28 || $[129] !== t30 || $[130] !== t35) {
    t37 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t28} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t30}{t35}{t36}</SidebarMenuButton></DropdownMenuTrigger>;
    $[128] = t28;
    $[129] = t30;
    $[130] = t35;
    $[131] = t37;
  } else {
    t37 = $[131];
  }
  let t38;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ExternalLink className="mr-2 h-4 w-4" />;
    $[132] = t38;
  } else {
    t38 = $[132];
  }
  let t39;
  if ($[133] !== dashboardUrl || $[134] !== labels.dashboard) {
    t39 = <DropdownMenuItem asChild={true}><a href={dashboardUrl} target="_blank" rel="noopener noreferrer">{t38}{labels.dashboard}</a></DropdownMenuItem>;
    $[133] = dashboardUrl;
    $[134] = labels.dashboard;
    $[135] = t39;
  } else {
    t39 = $[135];
  }
  let t40;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <DropdownMenuSeparator />;
    $[136] = t40;
  } else {
    t40 = $[136];
  }
  let t41;
  if ($[137] !== labels.theme) {
    t41 = <span>{labels.theme}</span>;
    $[137] = labels.theme;
    $[138] = t41;
  } else {
    t41 = $[138];
  }
  let t42;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="flex items-center gap-2"><div onClick={_temp6} onKeyDown={_temp7}><ThemeToggle className="shrink-0" size="compact" /></div></div>;
    $[139] = t42;
  } else {
    t42 = $[139];
  }
  let t43;
  if ($[140] !== t41) {
    t43 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp5}>{t41}{t42}</DropdownMenuItem>;
    $[140] = t41;
    $[141] = t43;
  } else {
    t43 = $[141];
  }
  let t44;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <DropdownMenuSeparator />;
    $[142] = t44;
  } else {
    t44 = $[142];
  }
  let t45;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <LogOut className="mr-2 h-4 w-4" />;
    $[143] = t45;
  } else {
    t45 = $[143];
  }
  let t46;
  if ($[144] !== labels.logOut || $[145] !== logout) {
    t46 = <DropdownMenuItem onClick={logout}>{t45}{labels.logOut}</DropdownMenuItem>;
    $[144] = labels.logOut;
    $[145] = logout;
    $[146] = t46;
  } else {
    t46 = $[146];
  }
  let t47;
  if ($[147] !== t39 || $[148] !== t43 || $[149] !== t46) {
    t47 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t39}{t40}{t43}{t44}{t46}</DropdownMenuContent>;
    $[147] = t39;
    $[148] = t43;
    $[149] = t46;
    $[150] = t47;
  } else {
    t47 = $[150];
  }
  let t48;
  if ($[151] !== t37 || $[152] !== t47) {
    t48 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t37}{t47}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[151] = t37;
    $[152] = t47;
    $[153] = t48;
  } else {
    t48 = $[153];
  }
  let t49;
  if ($[154] !== t27 || $[155] !== t48) {
    t49 = <SidebarFooter>{t27}{t48}</SidebarFooter>;
    $[154] = t27;
    $[155] = t48;
    $[156] = t49;
  } else {
    t49 = $[156];
  }
  let t50;
  if ($[157] !== handleShareSelect || $[158] !== isSearchOpen || $[159] !== organizationId) {
    t50 = <OrgSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} organizationId={organizationId} onShareSelect={handleShareSelect} />;
    $[157] = handleShareSelect;
    $[158] = isSearchOpen;
    $[159] = organizationId;
    $[160] = t50;
  } else {
    t50 = $[160];
  }
  let t51;
  if ($[161] !== t16 || $[162] !== t26 || $[163] !== t49 || $[164] !== t50 || $[165] !== t8) {
    t51 = <Sidebar collapsible="icon" className={t8}>{t16}{t26}{t49}{t50}</Sidebar>;
    $[161] = t16;
    $[162] = t26;
    $[163] = t49;
    $[164] = t50;
    $[165] = t8;
    $[166] = t51;
  } else {
    t51 = $[166];
  }
  return t51;
}
function _temp7(event_1) {
  return event_1.stopPropagation();
}
function _temp6(event_0) {
  return event_0.stopPropagation();
}
function _temp5(event) {
  return event.preventDefault();
}
function _temp4(n) {
  return n[0];
}
function _temp3(item) {
  return <SidebarMenuItem key={item.id}><SidebarMenuButton asChild={true} tooltip={item.tooltip}><Link href={item.route} prefetch={true}>{renderSidebarIcon(item.icon)}<span>{item.label}</span></Link></SidebarMenuButton></SidebarMenuItem>;
}
function _temp2(group) {
  return group.shares.length > 0;
}
function _temp(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}